"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.LandingPagesBuilderSchemaV2 = exports.FeaturesNewSection = void 0;
const zod_1 = require("zod");
const sections_1 = require("./sections");
const Bullets = zod_1.z
    .array(zod_1.z.object({
    text: zod_1.z.string().min(1, "Can't be empty"),
}))
    .optional()
    .default([]);
const commonFields = {
    enabled: zod_1.z.boolean(),
    bgColor: zod_1.z.string(),
    colorIndex: zod_1.z.number().optional(),
};
const IntroductionSection = zod_1.z.object({
    ...commonFields,
    type: zod_1.z.literal('Introduction'),
    enabled: zod_1.z.literal(true),
    title: zod_1.z.string().min(1, { message: 'Title is required' }).max(60, {
        message: 'Title is too long',
    }),
    subtitle: zod_1.z.string().min(1, { message: 'Subtitle is required' }).max(60, {
        message: 'Subtitle is too long',
    }),
    bullets: Bullets,
    coverImage: zod_1.z.string().min(1, { message: 'Cover image is required' }),
    introVideo: zod_1.z.string().nullable(),
});
const HeroFullVideoSection = zod_1.z.object({
    ...commonFields,
    type: zod_1.z.literal('HeroFullVideo'),
    enabled: zod_1.z.literal(true),
    title: zod_1.z.string().max(60, {
        message: 'Title is too long',
    }),
    bullets: Bullets,
    introVideo: zod_1.z.string().min(1, { message: 'Video is required' }),
});
const HeroFullImageSection = zod_1.z.object({
    ...commonFields,
    type: zod_1.z.literal('HeroFullImage'),
    enabled: zod_1.z.literal(true),
    title: zod_1.z.string().max(60, {
        message: 'Title is too long',
    }),
    bullets: Bullets,
    coverImage: zod_1.z.string().min(1, { message: 'Image is required' }),
});
const LogosSection = zod_1.z.object({
    ...commonFields,
    type: zod_1.z.literal('Logos'),
    logos: zod_1.z
        .array(zod_1.z.object({
        url: zod_1.z.string().min(1, { message: 'Logo URL is required' }),
    }))
        .min(3, { message: 'At least 3 logos are required' }),
});
const ReviewsSections = zod_1.z.object({
    ...commonFields,
    type: zod_1.z.literal('Reviews'),
    reviews: zod_1.z
        .array(zod_1.z.object({
        name: zod_1.z.string().min(1, { message: 'Name is required' }),
        text: zod_1.z.string().min(1, { message: 'Text is required' }),
    }))
        .optional()
        .default([]),
});
const TextSection2 = zod_1.z.object({
    ...commonFields,
    type: zod_1.z.literal('TextSection2'),
    title: zod_1.z.string().min(1, { message: 'Title is required' }),
    desc: zod_1.z.string().optional(),
    bullets: Bullets,
    images: zod_1.z
        .array(zod_1.z.object({ url: zod_1.z.string().nullable().default(null) }))
        .optional()
        .default([]),
    rotateImages: zod_1.z.boolean().optional(),
    swapSides: zod_1.z.boolean().optional(),
});
const TextSection1 = zod_1.z.object({
    ...commonFields,
    type: zod_1.z.literal('TextSection1'),
    title: zod_1.z.string().min(1, { message: 'Title is required' }),
    desc: zod_1.z.string().optional(),
    bullets: Bullets,
    coverImage: zod_1.z.string().min(1, { message: 'Cover image is required' }),
    introVideo: zod_1.z.string().nullable(),
    swapSides: zod_1.z.boolean().optional(),
});
const TextSection = zod_1.z.object({
    ...commonFields,
    type: zod_1.z.literal('TextSection'),
    title: zod_1.z.string().min(1, { message: 'Title is required' }),
    desc: zod_1.z.string().optional(),
});
const TestimonialsSection = zod_1.z.object({
    ...commonFields,
    type: zod_1.z.literal('Testimonials'),
    title: zod_1.z.string(),
    testimonials: zod_1.z
        .array(zod_1.z.object({
        video: zod_1.z.string().nullable().default(null),
        image: zod_1.z.string().nullable().default(null),
        name: zod_1.z.string().optional(),
        text: zod_1.z.string().optional(),
    }))
        .optional()
        .default([]),
});
const FaqsSection = zod_1.z.object({
    ...commonFields,
    type: zod_1.z.literal('FAQs'),
    title: zod_1.z.string(),
    faqs: zod_1.z
        .array(zod_1.z.object({
        question: zod_1.z.string().min(1, { message: 'Question is required' }),
        answer: zod_1.z.string().min(1, { message: 'Answer is required' }),
    }))
        .optional()
        .default([]),
});
const FeaturedBenefit = zod_1.z.object({
    ...commonFields,
    type: zod_1.z.literal('FeaturedBenefit'),
    title: zod_1.z.string().min(1, { message: 'Title is required' }),
    bullets: Bullets,
    coverImage: zod_1.z.string().min(1, { message: 'Cover image is required' }),
    swapSides: zod_1.z.boolean().optional(),
});
const ProgramsHighlight = zod_1.z.object({
    ...commonFields,
    type: zod_1.z.literal('ProgramsHighlight'),
    title: zod_1.z.string().min(1, { message: 'Title is required' }),
    bullets: Bullets,
    swapSides: zod_1.z.boolean().optional(),
    programIds: zod_1.z
        .array(zod_1.z.number())
        .min(1, { message: 'Select program to highlight' }),
});
const ProgramsList = zod_1.z.object({
    ...commonFields,
    type: zod_1.z.literal('ProgramsList'),
    title: zod_1.z.string(),
    subtitle: zod_1.z.string(),
    programIds: zod_1.z.array(zod_1.z.number()).optional(),
});
const PaidProgramsList = ProgramsList.extend({
    type: zod_1.z.literal('PaidProgramsList'),
});
const FeaturesSection = zod_1.z.object({
    ...commonFields,
    type: zod_1.z.literal('Features'),
    title: zod_1.z.string().optional(),
    remindersCoverImage: zod_1.z.string().nullable().default(null),
    streaksCoverImage: zod_1.z.string().nullable().default(null),
    workoutsCoverImage: zod_1.z.string().nullable().default(null),
    favoritesCoverImage: zod_1.z.string().nullable().default(null),
    lifetimeCoverImage: zod_1.z.string().nullable().default(null),
    streamCoverImage: zod_1.z.string().nullable().default(null),
});
const FeatureType = zod_1.z.union([
    zod_1.z.literal('reminders'),
    zod_1.z.literal('streaks'),
    zod_1.z.literal('tracking'),
    zod_1.z.literal('community'),
    zod_1.z.literal('favorites'),
    zod_1.z.literal('videos'),
    zod_1.z.literal('notes'),
    zod_1.z.literal('stream'),
    zod_1.z.literal('offline'),
    zod_1.z.literal('playlist'),
    zod_1.z.literal('nutrition'),
    zod_1.z.literal('messages'),
]);
exports.FeaturesNewSection = zod_1.z.object({
    ...commonFields,
    type: zod_1.z.literal('FeaturesNew'),
    title: zod_1.z.string().optional(),
    features: zod_1.z
        .array(zod_1.z.object({
        type: FeatureType,
        name: zod_1.z.string(),
        image: zod_1.z.string(),
    }))
        .length(6, { message: 'Select 6 features' }),
});
const BioSection = zod_1.z.object({
    ...commonFields,
    type: zod_1.z.literal('Bio'),
    title: zod_1.z.string().min(1, { message: 'Title is required' }),
    desc: zod_1.z.string().min(1, { message: 'Description is required' }),
    coverImage: zod_1.z.string().min(1, { message: 'Cover image is required' }),
    introVideo: zod_1.z.string().nullable(),
    swapSides: zod_1.z.boolean().optional(),
});
const Branding = zod_1.z.object({
    logo: zod_1.z.string().nullable().default(null),
    primaryColor: zod_1.z.string(),
});
const allowDisabled = (schema) => {
    return zod_1.z.union([
        schema,
        zod_1.z
            .object({ enabled: zod_1.z.literal(false), type: schema.shape.type })
            .passthrough(),
    ]);
};
exports.LandingPagesBuilderSchemaV2 = zod_1.z.object({
    branding: Branding,
    sections: zod_1.z.array(zod_1.z.union([
        IntroductionSection,
        HeroFullVideoSection,
        HeroFullImageSection,
        allowDisabled(sections_1.EmailCaptureSection),
        allowDisabled(LogosSection),
        allowDisabled(TextSection),
        allowDisabled(TextSection1),
        allowDisabled(TextSection2),
        allowDisabled(PaidProgramsList),
        allowDisabled(ProgramsList),
        allowDisabled(BioSection),
        allowDisabled(ReviewsSections),
        allowDisabled(TestimonialsSection),
        allowDisabled(FeaturesSection),
        allowDisabled(exports.FeaturesNewSection),
        allowDisabled(FaqsSection),
        allowDisabled(ProgramsHighlight),
        allowDisabled(FeaturedBenefit),
    ])),
});
